import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { DESKTOP_QUERY, useMediaQuery } from '@/hooks/useMediaQuery'
import type { totalsOf } from './peopleStats'

const people = (n: number) => (n === 1 ? '1 person' : `${n} people`)

export function PeopleSummary({ totals }: { totals: ReturnType<typeof totalsOf> }) {
  const { format: exact, formatWhole } = useFormatCurrency()
  const wide = useMediaQuery(DESKTOP_QUERY)
  // A phone hero is a glance, like the Dashboard and Accounts: whole rupees.
  const format = wide ? exact : formatWhole
  const { net, owedToUs, weOwe } = totals
  const headline =
    net > 0
      ? 'Overall, people owe you'
      : net < 0
        ? 'Overall, you owe people'
        : 'Overall, you’re even'

  return (
    <section
      aria-label="Overall"
      className="grid gap-2 rounded-2xl border border-[var(--fl-hero-border)] bg-gradient-to-br from-[var(--fl-hero-bg)] to-[var(--fl-hero-bg-2)] px-3.5 py-3 text-[var(--fl-hero-fg)] md:grid-cols-[1.3fr_1fr_1fr] md:items-end md:gap-6 md:rounded-[20px] md:p-6"
    >
      <div>
        <div className="flex items-baseline justify-between gap-3 md:block">
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--fl-hero-fg-2)] md:text-[13px] md:font-normal md:normal-case md:tracking-normal">
            {headline}
          </p>
          <p
            data-testid="net-total"
            className="shrink-0 font-serif text-[22px] font-bold tabular-nums tracking-tight md:mt-1 md:text-[40px] md:font-semibold"
          >
            {format(Math.abs(net))}
          </p>
        </div>
        {(owedToUs > 0 || weOwe > 0) && (
          <div
            aria-hidden="true"
            className="mt-2 flex h-1.5 gap-0.5 overflow-hidden rounded md:mt-3 md:h-2"
          >
            {owedToUs > 0 && <div className="bg-[var(--fl-hero-bar)]" style={{ flex: owedToUs }} />}
            {weOwe > 0 && (
              <div className="bg-[var(--fl-hero-expense)] opacity-70" style={{ flex: weOwe }} />
            )}
          </div>
        )}
      </div>
      <div className="flex justify-between gap-3 md:contents">
        <div className="flex items-baseline gap-1.5 md:block">
          <p className="text-xs text-[var(--fl-hero-fg-2)] md:text-[13px]">
            Owed to you
            <span className="hidden md:inline"> · {people(totals.owedToUsCount)}</span>
          </p>
          <p
            data-testid="owed-to-us-total"
            className="text-[13px] font-semibold tabular-nums text-[var(--fl-hero-income)] md:mt-0.5 md:text-[22px]"
          >
            {format(owedToUs)}
          </p>
        </div>
        <div className="flex items-baseline gap-1.5 md:block">
          <p className="text-xs text-[var(--fl-hero-fg-2)] md:text-[13px]">
            You owe
            <span className="hidden md:inline"> · {people(totals.weOweCount)}</span>
          </p>
          <p
            data-testid="we-owe-total"
            className="text-[13px] font-semibold tabular-nums text-[var(--fl-hero-expense)] md:mt-0.5 md:text-[22px]"
          >
            {format(weOwe)}
          </p>
        </div>
      </div>
    </section>
  )
}
